import { FlaskConical, Mail, LayoutDashboard, Target, Trophy, Contact as ContactsIcon, ClipboardList, FolderKanban, CalendarDays, Users, CalendarRange, CalendarClock, Settings, BarChart3, LineChart, Activity, Megaphone, Sparkles, BrainCircuit, Radar, Headphones, MonitorPlay, FileText, FileStack, NotebookPen, ShieldAlert, Banknote, Wallet, Network, ArrowLeftRight, ShoppingCart, Receipt, ReceiptText, Landmark, Package, CheckSquare, Search, ScrollText, LifeBuoy, ServerCog, BriefcaseBusiness, Radio, UserCog, MessagesSquare, Fingerprint, Shield, Database, Inbox, MapPinned, Tag, Star, Building2, Truck, PiggyBank, Warehouse, Lock, ListChecks, Coins, Clock, FileUp, Award, Store, FileSpreadsheet, UserMinus, UsersRound, ScanLine, Boxes, Palette, Globe, FileArchive, Trash2, type LucideIcon } from 'lucide-react';
import { tierOf } from '@/shared/permissions/identity';
import { isRbacAllowed, type RbacPolicy } from '@/shared/permissions/rbac';
import { defaultWorkspaceAccess, isRohitSharmaUser, type WorkspaceAccessKey } from '@/shared/permissions/workspaceAccess';
import type { ZynxIconName, ZynxTone } from '@/shared/components/ui/ZynxIcon';

export const ROUTES = {
  root: '/',
  login: '/login',
  // Field Executive workspace (2026-10-07).
  fieldInvoiceRequests: '/field/invoice-requests',
  fieldApprovals: '/field-approvals',
  eJoining: '/e-joining/:token',
  dashboard: '/dashboard',
  // ── The unified Sales Pipeline (Leads + Deals merge) ─────────────────────
  // One menu for what used to be two. `leads` and `deals` are kept here, but
  // NOT as menu items — they exist so AppRouter can redirect them. Bookmarks,
  // links pasted into WhatsApp, and the dialer APK's deep links all still point
  // at the old paths, and breaking them to save two lines would be a poor trade.
  pipeline: '/pipeline',
  pipelineNew: '/pipeline/new',
  pipelineDetail: '/pipeline/:id',
  pipelineEdit: '/pipeline/:id/edit',
  // Super Admin only — see requireSuperAdmin on the /api/pipeline/trash*
  // routes. Everything a "Move to Trash" cascaded onto (client profile,
  // contacts, quotations, samples, delivery challans) is invisible to
  // everyone else until restored or permanently deleted from here.
  pipelineTrash: '/pipeline/trash',
  leads: '/leads',
  deals: '/deals',
  dealDetail: '/deals/:id',
  contacts: '/contacts',
  contactNew: '/contacts/new',
  contactDetail: '/contacts/:id',
  contactEdit: '/contacts/:id/edit',
  tasks: '/tasks',
  projects: '/projects',
  meetings: '/meetings',
  hr: '/hr',
  hrDashboard: '/hr/dashboard',
  employee360: '/hr/people/user/:userId',
  organization: '/hr/organization',
  leaves: '/leaves',
  attendance: '/attendance',
  biometricAdmin: '/attendance/biometric',
  settings: '/settings',
  reports: '/reports',
  activity: '/activity',
  analytics: '/analytics',
  announcements: '/announcements',
  ai: '/ai',
  aiCopilot: '/ai-copilot',
  scraper: '/scraper',
  callCenter: '/call-center',
  demos: '/demos',
  salesDocs: '/sales-docs',
  quotations: '/quotations',
  // The field-based quotation form. Accepts ?dealId= to prefill from a pipeline
  // record. Deliberately NOT a nav entry — it is reached from the Quotations
  // list and from a pipeline record, and a section gate on the list already
  // covers both.
  quotationNew: '/quotations/new',
  /** Editing reuses the same form. Drafts only — an issued quotation is
   *  changed by Revise, which produces a new draft to edit. */
  quotationEdit: '/quotations/:id/edit',
  quotationDetail: '/quotations/:id',
  proforma: '/proforma',
  proformaNew: '/proforma/new',
  proformaEdit: '/proforma/:id/edit',
  proformaDetail: '/proforma/:id',
  invoices: '/invoices',
  invoiceEdit: '/invoices/:id/edit',
  invoiceDetail: '/invoices/:id',
  /** Admin/Superadmin queue for edits requested against a finalized Invoice
   *  or Tax Invoice — see src/api-invoice-edit-requests/index.js. */
  invoiceEditApprovals: '/invoices/edit-approvals',
  visits: '/visits',
  notes: '/notes',
  complaints: '/complaints',
  serviceDesk: '/service-desk',
  itOperations: '/it-operations',
  recruitment: '/recruitment',
  liveTracking: '/live-tracking',
  fleetManagement: '/fleet-management',
  driverPortal: '/driver-portal',
  chat: '/chat',
  commHub: '/communication-hub',
  portalAccounts: '/portal-accounts',
  portal: '/portal',
  payroll: '/payroll',
  // ledger ('/ledger') deleted 2026-08-23: a thin wrapper around the same
  // ExpenseLedgerWidget a dashboard widget already uses, fully superseded by
  // the real double-entry Ledger Explorer (accountingV2Transactions below).
  // The widget itself is untouched — only this standalone page is gone.
  // Accounts — nested routes
  accounts: '/accounts',
  accountsTransactions: '/accounts/transactions',
  accountsSales: '/accounts/sales',
  accountsHandoffs: '/accounts/sales-handoffs',
  accountsPurchases: '/accounts/purchases',
  accountsParties: '/accounts/parties',
  accountsExpenses: '/accounts/expenses',
  accountsBanking: '/accounts/banking',
  accountsBudgets: '/accounts/budgets',
  accountsAssets: '/accounts/assets',
  accountsReports: '/accounts/reports',
  accountsSearch: '/accounts/search',
  accountsSettings: '/accounts/settings',
  accountsAudit: '/accounts/audit',
  accountsTeam: '/accounts/team',
  accountsTransactionLocking: '/accounts/transaction-locking',
  accountsBulkUpdate: '/accounts/bulk-update',
  accountsCurrencyAdjustments: '/accounts/currency-adjustments',
  accountsTimeTracking: '/accounts/time-tracking',
  accountsDocuments: '/accounts/documents',
  accountsImports: '/accounts/imports',
  catalog: '/catalog',
  inventory: '/inventory',
  /**
   * Read-only valuation and risk screens over /api/accounting/inventory:
   * stock card, costing-method valuation, low-stock, ageing and slow-moving
   * alerts. Separate from /inventory because that page is the operational
   * day-work surface on /api/inventory; this one is the ledger engine's.
   */
  inventoryAnalysis: '/inventory/analysis',
  /**
   * Read-only books over /api/accounts: bank book, petty cash, daily
   * collection, daily payment, monthly summary. Not a second Banking page —
   * those are the fixed-head report endpoints in api-accounts/reports-advanced.js.
   */
  accountsBankCashReports: '/accounts/bank-cash-reports',
  /** Receivables and payables ageing: /api/accounts/reports/{customer,vendor}-aging. */
  accountsAgingReports: '/accounts/aging-reports',
  procurement: '/procurement',
  salesOperations: '/sales-operations',
  salesOrderDetail: '/sales-orders/:id',
  /** Sales-only, read-only view of the Operations fulfilment progress. */
  salesDispatchTracking: '/sales/pending-dispatch',
  dispatchOrders: '/dispatch/orders',
  /** One order's picking sheet. Not a nav entry — reached from the queue. */
  dispatchOrderDetail: '/dispatch/orders/:id',
  dispatchChallans: '/dispatch/challans',
  /** What each dispatch's local tempo / Porter cost — Dispatch + Accounts. */
  dispatchLocalTransport: '/dispatch/local-transport',
  /** Payment proof, LR / e-way bill and invoice copies for one challan. Not a
   *  nav entry — reached from the challan's own row. */
  dispatchChallanDocuments: '/dispatch/challans/:id/documents',
  pos: '/pos',
  operationalReports: '/operational-reports',
  notificationTemplates: '/notification-templates',
  // HR extended routes
  performance: '/performance',
  incentives: '/incentives',
  hrOnboarding: '/hr-onboarding',
  hrOnboardingDocument: '/hr-onboarding/:candidateId/documents/:docCode',
  hrDocumentTemplates: '/hr/document-templates',
  // hrBranding ('/hr/branding') deleted 2026-08-23: two route constants for
  // one page (HrBrandingPage.tsx, which titles itself "Letterhead Branding"
  // and was never registered in AppRouter under either path — restored below
  // under this one, canonical name).
  hrLetterheadBranding: '/hr/letterhead-branding',
  offerLetters: '/offer-letters',
  // Hidden executive surface
  commandCenter: '/command-center',
  oversight: '/oversight',
  // Accounting V2 — double-entry ledger surface. Paths mirror the live VPS
  // build exactly so deep links and bookmarks keep working after this merge.
  accountingV2: '/accounting-v2',
  accountingV2Coa: '/accounting-v2/coa',
  accountingV2Transactions: '/accounting-v2/transactions',
  accountingV2Journals: '/accounting-v2/journals',
  accountingV2Vouchers: '/accounting-v2/vouchers',
  accountingV2Parties: '/accounting-v2/parties',
  accountingV2Items: '/accounting-v2/items',
  accountingV2ItemDetail: '/accounting-v2/items/:itemId',
  accountingV2P2p: '/accounting-v2/p2p',
  // RFQ — the multi-supplier quotation step ahead of the purchase order. See
  // AppRouter's route comment for why the list is registered before the
  // detail route.
  accountingV2Rfqs: '/accounting-v2/rfqs',
  accountingV2RfqDetail: '/accounting-v2/rfqs/:id',
  accountingV2SupplierScorecards: '/accounting-v2/supplier-scorecards',
  accountingV2Settings: '/accounting-v2/accounting-settings',
  accountingV2Expenses: '/accounting-v2/expenses',
  accountingV2ExpenseDetail: '/accounting-v2/expenses/:id',
  accountingV2Approvals: '/accounting-v2/approvals',
  accountingV2Commissions: '/accounting-v2/commissions',
  accountingV2Reports: '/accounting-v2/reports',
  accountingV2AuditLogs: '/accounting-v2/audit-logs',
  accountingV2Pdc: '/accounting-v2/pdc',
  accountingV2Barcode: '/accounting-v2/barcode',
  accountingV2Services: '/accounting-v2/services',
  accountingV2Rejections: '/accounting-v2/rejections',
  // These four render inside the Accounting V2 shell (mounted in AppRouter,
  // gated on access="accounts") but are not yet linked from AccountingSubNav �
  // reachable today only by direct URL or programmatic navigation.
  accountingGstr1: '/accounting-v2/gstr1',
  accountingGstr2: '/accounting-v2/gstr2',
  accountingGstr3: '/accounting-v2/gstr3',
  workflowsAutomations: '/accounting-v2/workflows-automations',
  bankingPaymentAccounts: '/accounting-v2/banking/payment-accounts',
  bankingBankAccounts: '/accounting-v2/banking/bank-accounts',
  bankingEmployeeAccounts: '/accounting-v2/banking/employee-accounts',
  bankingReconciliation: '/accounting-v2/banking/reconciliation',
  // Manufacturing — production, BOMs, batches, quality and costing.
  manufacturing: '/manufacturing',
  manufacturingOrders: '/manufacturing/orders',
  manufacturingBoms: '/manufacturing/boms',
  manufacturingBatchOps: '/manufacturing/batch-ops',
  manufacturingPackaging: '/manufacturing/packaging',
  manufacturingQuality: '/manufacturing/quality',
  manufacturingTraceability: '/manufacturing/traceability',
  manufacturingMasterData: '/manufacturing/master-data',
  manufacturingCosting: '/manufacturing/costing',
  manufacturingOilScenario: '/manufacturing/oil-scenario',
  // Services & Projects � pre-sale (rate cards, proposals, contracts) through
  // delivery (projects, timesheets) to money (billing, revenue, margin).
  svcRateCards: '/services/rate-cards',
  svcProposals: '/services/proposals',
  svcContracts: '/services/contracts',
  svcProjects: '/services/projects',
  svcTimesheets: '/services/timesheets',
  svcBilling: '/services/billing',
  svcRevenueRecognition: '/services/revenue-recognition',
  svcProfitability: '/services/profitability',
  // Sales' own client/prospect list and its creation flows, gated on
  // access="sales" / "sales_documents" / "sales_operations" rather than the
  // services_projects gate the "svc" prefix above uses.
  svcClientsProspects: '/sales/clients-prospects',
  svcClientProspectDetail: '/sales/clients-prospects/:id',
  salesOrderCreate: '/sales-operations/orders/create',
  documentEditor: '/documents/create',
  // Settings sub-pages and master data. Paths match the live VPS build; their
  // components already existed but had no ROUTES entry, which is why
  // SettingsSubNav.tsx and the master-data pages failed to compile.
  settingsCompany: '/settings/company',
  settingsLocations: '/settings/locations',
  settingsFiscal: '/settings/fiscal',
  settingsNumbering: '/settings/numbering',
  settingsDocumentTemplates: '/settings/document-templates',
  settingsIntegrations: '/settings/integrations',
  // RBM website integration (src/integrations/adapters/website.js on the
  // backend) — the data side, distinct from Settings → Integrations →
  // Website, which is the connection/credentials side.
  websiteLeads: '/website/leads',
  websiteDocuments: '/website/documents',
  settingsGstRegistrations: '/settings/gst-registrations',
  settingsCrmSetup: '/settings/crm-setup',
  gstFiling: '/gst-filing',
  campaigns: '/campaigns',
  adminTools: '/admin-tools',
  ewayBill: '/eway-bill',
  mailInbox: '/mail-inbox',
  leadInbox: '/lead-inbox',
  targets: '/targets',
  /** Sales Performance & Incentive Management (2026-10-06). */
  salesPerformance: '/sales-performance',
  salesPerformanceEmployee: '/sales-performance/employee/:id',
  reportSuite: '/report-suite',
  settingsModules: '/settings/modules',
  settingsLocale: '/settings/locale',
  masterData: '/master-data',
  // departmentMaster/designationMaster/salaryStructureMaster/
  // salaryComponentMaster deleted 2026-08-23: never routed under these named
  // constants at all — MasterDataPage.tsx actually navigates via the generic
  // `/master-data/:groupKey/:key` route (masterDataGeneric below covers the
  // single-segment `/master-data/:masterType` shape; masterDataSpecial covers
  // `/master-data/special/:kind`), and those are what's real. companyProfileMaster
  // stays: HrBrandingPage.tsx and others link to it directly by name.
  companyProfileMaster: '/master-data/company-profile',
  masterDataGeneric: '/master-data/:masterType',
  masterDataSpecial: '/master-data/special/:kind',
  roles: '/roles',
  sampling: '/sampling',
  // The sample request form is a PAGE, not a dialog — it is a long form with
  // its own record picker. `?recordId=` pre-links it, which is how a pipeline
  // record's own sampling panel opens it.
  samplingNew: '/sampling/new',
  samplingEdit: '/sampling/:id/edit',
  clients: '/clients',
  clientProfile: '/clients/:id',
  leaveMasterData: '/leaves/master-data',
  shiftMasterData: '/attendance/shift-master-data',
  myProfile: '/my-profile',
  vouchers: '/vouchers',
  loyalty: '/loyalty',
  rewards: '/rewards',
  taxDeclarations: '/hr/tax-declarations',
  fnfSettlement: '/hr/fnf',
  hrOnboardingDetail: '/hr/onboarding/:id',
  recruitmentApplication: '/recruitment/applications/:applicationId',
} as const;

export interface NavVisibilityPolicy extends RbacPolicy {
  path?: string;
  depts?: string[];
  excludeDepts?: string[];
  /** Workspace module gate resolved from department defaults + custom grants. */
  /** One module key, or several meaning "any of these grants access". */
  access?: WorkspaceAccessKey | readonly WorkspaceAccessKey[];
  /**
   * The server checks the DEPARTMENT too, not just the module (every Accounts
   * route sits behind requireAccountsAccess: accounts department or an admin).
   * Holding the module alone must not show the item — Operations holds
   * 'accounts' by default and saw ~20 Accounts pages that all 403'd.
   */
  deptEnforced?: boolean;
  /** Roles required within a given department, mirroring a server rule (Payroll: Accounts admins only). */
  deptRoles?: Readonly<Record<string, readonly string[]>>;
  /** The server refuses this screen's data to anyone the sidebar hides it
   *  from, so the URL is closed to them as well (see navPathAllowed). */
  routeGuarded?: boolean;
}

export interface NavItem extends NavVisibilityPolicy {
  label: string;
  path: string;
  icon: LucideIcon;
  /**
   * When set, the rail draws Zynx's own mark instead of the lucide `icon`.
   * This exists because 46 of the 198 nav entries shared a glyph with an
   * unrelated destination, so the rail could not be scanned without reading
   * every label. `icon` stays required: it is still what the collapsed
   * fallbacks, breadcrumbs and search results draw.
   */
  zynxIcon?: ZynxIconName;
  zynxTone?: ZynxTone;
}

export interface NavGroup {
  /** Section label shown above the group; omit for the top-level group. */
  label?: string;
  items: NavItem[];
}

/**
 * Sidebar navigation. Foundation phase ships ONLY Dashboard; each migrated
 * module (Step 4+) appends its entry here. Do not add CRM modules yet.
 */
export const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: ROUTES.dashboard, icon: LayoutDashboard, zynxIcon: 'dashboard', excludeDepts: ['accounts'] },
  { label: 'Sales Pipeline', path: ROUTES.pipeline, icon: Target, zynxIcon: 'pipeline', depts: ['sales'] },
  { label: 'Lead Generation', path: ROUTES.scraper, icon: Radar, zynxIcon: 'radar', depts: ['sales'] },
  { label: 'Call Center', path: ROUTES.callCenter, icon: Headphones, depts: ['sales'] },
  { label: 'Contacts', path: ROUTES.contacts, icon: ContactsIcon, zynxIcon: 'contacts', depts: ['sales'] },
  { label: 'Tasks', path: ROUTES.tasks, icon: ClipboardList, zynxIcon: 'tasks' },
  { label: 'Projects', path: ROUTES.projects, icon: FolderKanban },
  { label: 'Project Demos', path: ROUTES.demos, icon: MonitorPlay },
  { label: 'Quotations', path: ROUTES.quotations, icon: FileText, zynxIcon: 'quote', depts: ['sales', 'accounts'] },
  { label: 'Proforma Invoices', path: ROUTES.proforma, icon: FileStack, depts: ['sales', 'accounts'] },
  { label: 'Invoices', path: ROUTES.invoices, icon: ReceiptText, zynxIcon: 'invoice', depts: ['sales', 'accounts'] },
  { label: 'Document Studio', path: ROUTES.documentEditor, icon: ReceiptText, depts: ['sales', 'accounts'] },
  { label: 'Sales Documents', path: ROUTES.salesDocs, icon: FileText, zynxIcon: 'docs', depts: ['sales', 'accounts'] },
  { label: 'Sales Orders', path: ROUTES.salesOperations, icon: ClipboardList, zynxIcon: 'orders', depts: ['sales', 'accounts'] },
  { label: 'Visit Planner', path: ROUTES.visits, icon: MapPinned, zynxIcon: 'visit', depts: ['sales'] },
  { label: 'Live GPS Radar', path: ROUTES.liveTracking, icon: Radio, zynxIcon: 'radar', depts: ['sales'] },
  { label: 'Notepad', path: ROUTES.notes, icon: NotebookPen },
  { label: 'Meetings', path: ROUTES.meetings, icon: CalendarDays },
  { label: 'Service Desk', path: ROUTES.serviceDesk, icon: LifeBuoy, zynxIcon: 'support' },
  { label: 'IT Operations', path: ROUTES.itOperations, icon: ServerCog, depts: ['it'] },
  { label: 'Announcements', path: ROUTES.announcements, icon: Megaphone },
  { label: 'Team & Access', path: ROUTES.hr, icon: Users, zynxIcon: 'contacts', roles: ['admin', 'superadmin'] },
  { label: 'Leaves', path: ROUTES.leaves, icon: CalendarRange, zynxIcon: 'leaves' },
  { label: 'Attendance', path: ROUTES.attendance, icon: CalendarClock, zynxIcon: 'attendance' },
  { label: 'Activity', path: ROUTES.activity, icon: Activity },
  { label: 'AI Workspace', path: ROUTES.ai, icon: Sparkles },
  { label: 'AI Copilot', path: ROUTES.aiCopilot, icon: BrainCircuit },
  { label: 'Analytics', path: ROUTES.analytics, icon: LineChart, zynxIcon: 'analytics', roles: ['admin', 'superadmin'] },
  { label: 'Reports', path: ROUTES.reports, icon: BarChart3, roles: ['admin', 'superadmin'] },
  { label: 'Settings', path: ROUTES.settings, icon: Settings, zynxIcon: 'settings' },
];

/**
 * Grouped navigation for the enterprise sidebar. Logical sections keep the
 * ~20 modules scannable. Role gating is identical to NAV_ITEMS (UI-only;
 * the server stays authoritative). NAV_ITEMS is retained for compatibility.
 */
export const NAV_GROUPS: NavGroup[] = [
  // ── Layout ────────────────────────────────────────────────────────────────
  // Grouped the way RBM actually works — Sales → Dispatch → Accounts, then
  // the supporting modules — so each team finds its own work in one block.
  // Every item keeps the exact visibility gate (depts / access / roles /
  // authorityTiers) it had before this reordering; only its group and
  // position changed. Section-level grants key on the item PATH (see
  // navSectionVisible below), so regrouping never changes who can open what.
  // Gate rationale for the individual items lives in git history
  // (git log -p -- frontend/src/routes/paths.ts).
  {
    items: [
      { label: 'Dashboard', path: ROUTES.dashboard, icon: LayoutDashboard, zynxIcon: 'dashboard', excludeDepts: ['accounts'] },
      // Every employee applies for leave from here; approvers also review it.
      { label: 'Leave Request', path: ROUTES.leaves, icon: CalendarRange, zynxIcon: 'leaves' },
    ],
  },

  // ── SALES ─────────────────────────────────────────────────────────────────
  {
    label: 'Sales',
    items: [
      { label: 'Sales Pipeline', path: ROUTES.pipeline, icon: Target, zynxIcon: 'pipeline', depts: ['sales', 'general_manager'], access: 'sales' },
      { label: 'Contacts', path: ROUTES.contacts, icon: ContactsIcon, zynxIcon: 'contacts', depts: ['sales', 'general_manager'], access: 'sales' },
      { label: 'Client Profiles', path: ROUTES.clients, icon: Building2, depts: ['sales', 'accounts', 'management', 'general_manager'], access: 'sales' },
      // Shared with Dispatch (they send the sample out) — see the /api/sampling
      // gate in src/workspace-access.js, which names both modules too.
      { label: 'Sampling & Trials', path: ROUTES.sampling, icon: FlaskConical, zynxIcon: 'trial', depts: ['sales', 'dispatch', 'general_manager'], access: ['sales', 'dispatch'] },
      { label: 'Quotations', path: ROUTES.quotations, icon: FileText, zynxIcon: 'quote', depts: ['sales', 'accounts', 'management', 'general_manager'], access: 'sales' },
      { label: 'Proforma Invoices', path: ROUTES.proforma, icon: FileStack, depts: ['sales', 'accounts', 'management', 'general_manager'], access: 'sales' },
      // Accounts filters invoices by date and client here (with the period total)
      // — they hold Sales-Operations/Accounts, not the Sales workspace.
      { label: 'Invoices', path: ROUTES.invoices, icon: ReceiptText, zynxIcon: 'invoice', depts: ['sales', 'accounts', 'management', 'general_manager'], access: ['sales', 'sales_operations', 'accounts'] },
      { label: 'Sales Orders', path: ROUTES.salesOperations, icon: ClipboardList, zynxIcon: 'orders', depts: ['sales', 'accounts', 'general_manager'], access: 'sales_operations' },
      { label: 'Document Studio', path: ROUTES.documentEditor, icon: ReceiptText, depts: ['sales', 'accounts', 'management', 'general_manager'], access: 'sales' },
      // Nav and route now gate this screen the same way (AppRouter: WorkspaceRoute
      // access=['catalog','sales','accounts','inventory']). It used to ask for
      // ['sales','catalog'] PLUS a department whitelist, so a user explicitly
      // granted the Accounts or Inventory module could open /catalog but never saw
      // the link — the grant appeared to do nothing. The department cap is dropped
      // because the module axis is what decides a screen: department only decides
      // which modules a user INHERITS by default.
      { label: 'Products & Catalog', path: ROUTES.catalog, icon: Package, zynxIcon: 'stock', access: ['catalog', 'sales', 'accounts', 'inventory'] },
      // Targets are set by Superadmin / Admin / Department Administrator only;
      // employees see their own target on their dashboard (2026-10-06).
      { label: 'Sales Targets', path: ROUTES.targets, icon: Target, zynxIcon: 'target', depts: ['sales', 'accounts', 'general_manager'], roles: ['superadmin', 'admin', 'department_admin', 'bde', 'employee'] },
      // Everyone who sells sees their own numbers; management, HR and Accounts
      // see the team (the server decides — src/api-sales-performance).
      { label: 'Sales Performance', path: ROUTES.salesPerformance, icon: Trophy, zynxIcon: 'reward', access: ['sales', 'hr', 'accounts'] },
      { label: 'Client Complaints', path: ROUTES.complaints, icon: LifeBuoy, zynxIcon: 'alert', depts: ['sales', 'management', 'general_manager'], access: ['sales', 'service_desk'] },
    ],
  },
  {
    label: 'Leads & Marketing',
    items: [
      { label: 'Incoming Leads', path: ROUTES.leadInbox, icon: Inbox, access: ['scraper', 'sales'], excludeDepts: ['accounts', 'dispatch'] },
      { label: 'Website Leads', path: ROUTES.websiteLeads, icon: Globe, depts: ['sales', 'general_manager'], access: 'sales', excludeDepts: ['accounts', 'dispatch'] },
      { label: 'Lead Generation', path: ROUTES.scraper, icon: Radar, zynxIcon: 'radar', depts: ['sales', 'general_manager'], access: 'scraper' },
      { label: 'Call Center', path: ROUTES.callCenter, icon: Headphones, depts: ['sales', 'general_manager'], access: 'call_center' },
      { label: 'TDS / MSDS Library', path: ROUTES.websiteDocuments, icon: FileArchive, depts: ['sales', 'general_manager'], access: 'sales', excludeDepts: ['accounts', 'dispatch'] },
      { label: 'Vouchers & Promos', path: ROUTES.vouchers, icon: Tag, depts: ['sales', 'general_manager'], access: 'sales', excludeDepts: ['accounts', 'dispatch'] },
      { label: 'Loyalty Points', path: ROUTES.loyalty, icon: Star, depts: ['sales', 'general_manager'], access: 'sales', excludeDepts: ['accounts', 'dispatch'] },
      { label: 'Point of Sale', path: ROUTES.pos, icon: Store, depts: ['sales', 'general_manager'], access: 'sales', excludeDepts: ['accounts', 'dispatch'] },
    ],
  },
  {
    label: 'Field Sales',
    items: [
      { label: 'Visit Planner', path: ROUTES.visits, icon: MapPinned, zynxIcon: 'visit', depts: ['sales', 'general_manager'], access: 'sales', excludeDepts: ['accounts', 'dispatch', 'operations', 'production'], routeGuarded: true },
      { label: 'Live GPS Radar', path: ROUTES.liveTracking, icon: Radio, zynxIcon: 'radar', depts: ['sales', 'general_manager'], access: 'sales', excludeDepts: ['accounts', 'dispatch'] },
    ],
  },

  // ── DISPATCH ──────────────────────────────────────────────────────────────
  {
    label: 'Dispatch',
    items: [
      // Sales tracks its own orders here and Accounts has access too (client
      // request #104) — one queue, three teams.
      { label: 'Pending Dispatch', path: ROUTES.salesDispatchTracking, icon: Truck, depts: ['sales', 'accounts', 'dispatch'], access: ['sales_operations', 'accounts', 'dispatch'] },
      { label: 'Dispatch & Challans', path: ROUTES.dispatchOrders, icon: Truck, zynxIcon: 'challan', access: ['dispatch', 'accounts', 'sales'] },
      // Transport amounts and tracking (local + third-party) — available for management, sales, and accounts.
      { label: 'Transport Costs', path: ROUTES.dispatchLocalTransport, icon: Coins, depts: ['sales', 'dispatch', 'accounts'], roles: ['superadmin', 'admin', 'department_admin', 'bde', 'employee'] },
      { label: 'Field Requests', path: ROUTES.fieldApprovals, icon: CheckSquare, depts: ['sales', 'dispatch', 'accounts'], roles: ['superadmin', 'admin', 'department_admin', 'bde', 'employee'] },
      // Drivers and dispatch desk — shared with sales and dispatch.
      { label: 'Fleet & Drivers', path: ROUTES.fleetManagement, icon: Truck, zynxIcon: 'fleet', access: ['dispatch', 'sales'] },
    ],
  },
  {
    label: 'Inventory',
    items: [
      // Dispatch raises material requests and records inward here (client,
      // 2026-10-24); the route already admits ['dispatch', 'inventory'].
      { label: 'Inventory', path: ROUTES.inventory, icon: Boxes, zynxIcon: 'stock', access: ['inventory', 'dispatch'], depts: ['inventory', 'dispatch', 'production', 'management'], routeGuarded: true },
      // Read-only valuation/alerts on the same ledger-inventory surface the
      // Inventory page's advanced tabs already read, so it carries the exact
      // gate as that page: the same module keys, the same departments, and the
      // same routeGuarded flag that closes the URL to anyone the menu hides it
      // from. Server side this surface is
      // [['accounts','dispatch','inventory'], ['/api/accounting/inventory', …]]
      // (src/workspace-access.js), which is wider than the menu on purpose —
      // Accounts reach these figures through Financial Reports instead.
      { label: 'Stock Analysis & Valuation', path: ROUTES.inventoryAnalysis, icon: ScrollText, access: 'inventory', depts: ['inventory', 'dispatch', 'production', 'management'], routeGuarded: true },
      // "Item Master" was removed (client, 2026-10-24): it listed and edited the
      // same product records as Products & Catalog, with service-business
      // extras. Stock settings live in Inventory → Stock Control.
      { label: 'Print Barcodes', path: ROUTES.accountingV2Barcode, icon: Package, depts: ['dispatch', 'accounts', 'inventory', 'production', 'management'], access: ['dispatch', 'accounts'] },
      { label: 'Rejection In/Out', path: ROUTES.accountingV2Rejections, icon: ArrowLeftRight, depts: ['dispatch', 'accounts', 'inventory', 'production', 'management'], access: ['dispatch', 'accounts'] },
    ],
  },

  // ── ACCOUNTS ──────────────────────────────────────────────────────────────
  {
    label: 'Accounts',
    items: [
      { label: 'Accounts Dashboard', path: ROUTES.accountingV2, icon: LayoutDashboard, zynxIcon: 'dashboard', depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Sales & Receipts', path: ROUTES.accountsSales, icon: ReceiptText, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Sales Handoffs', path: ROUTES.accountsHandoffs, icon: FileText, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Sales Documents', path: ROUTES.salesDocs, icon: FileText, zynxIcon: 'docs', depts: ['accounts'], access: 'sales_documents', excludeDepts: ['sales', 'dispatch'] },
      { label: 'Invoice Edit Approvals', path: ROUTES.invoiceEditApprovals, icon: CheckSquare, depts: ['accounts'], roles: ['admin', 'superadmin'] },
      { label: 'Receipts & Payments', path: ROUTES.accountingV2Vouchers, icon: Banknote, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Post-Dated Cheques', path: ROUTES.accountingV2Pdc, icon: Landmark, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Customers & Suppliers', path: ROUTES.accountingV2Parties, icon: Users, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Bank Reconciliation', path: ROUTES.bankingReconciliation, icon: Landmark, depts: ['accounts'], roles: ['admin', 'superadmin', 'department_admin'], access: 'accounts', deptEnforced: true },
      { label: 'Expenses', path: ROUTES.accountingV2Expenses, icon: Receipt, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Approval Inbox', path: ROUTES.accountingV2Approvals, icon: CheckSquare, zynxIcon: 'approve', depts: ['accounts'], access: 'accounts', deptEnforced: true },
    ],
  },
  {
    label: 'Purchases',
    items: [
      { label: 'Procure-to-Pay', path: ROUTES.accountingV2P2p, icon: ShoppingCart, depts: ['accounts', 'sales'], access: ['accounts', 'sales', 'procurement'], deptEnforced: true },
      { label: 'RFQs', path: ROUTES.accountingV2Rfqs, icon: FileStack, depts: ['accounts', 'sales'], access: ['accounts', 'sales', 'procurement'], deptEnforced: true },
      { label: 'Supplier Scorecards', path: ROUTES.accountingV2SupplierScorecards, icon: Star, depts: ['accounts', 'sales'], access: ['accounts', 'sales', 'procurement'], deptEnforced: true },
    ],
  },
  {
    label: 'GST & Compliance',
    items: [
      { label: 'GST Return Filing', path: ROUTES.gstFiling, icon: ReceiptText, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'GSTR-1', path: ROUTES.accountingGstr1, icon: ScrollText, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'GSTR-2', path: ROUTES.accountingGstr2, icon: ScrollText, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'GSTR-3', path: ROUTES.accountingGstr3, icon: ScrollText, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'E-way Bills', path: ROUTES.ewayBill, icon: Package, depts: ['accounts'], access: 'accounts', deptEnforced: true },
    ],
  },
  {
    label: 'Finance & Ledger',
    items: [
      { label: 'Chart of Accounts', path: ROUTES.accountingV2Coa, icon: Landmark, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Ledger Explorer', path: ROUTES.accountingV2Transactions, icon: ArrowLeftRight, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Manual Journals', path: ROUTES.accountingV2Journals, icon: ScrollText, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Financial Reports', path: ROUTES.accountingV2Reports, icon: BarChart3, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      // The accounts-module report endpoints that had no screen. Same gate as
      // every other Finance & Ledger item — the `accounts` module AND the
      // accounts department (deptEnforced), because api-accounts answers
      // requireAccountsAccess: accounts dept, admin or above, nothing less. A
      // user outside it is refused by the server, so the menu refuses too.
      { label: 'Cash & Bank Reports', path: ROUTES.accountsBankCashReports, icon: Wallet, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Receivables & Payables Ageing', path: ROUTES.accountsAgingReports, icon: FileSpreadsheet, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Budgets', path: ROUTES.accountsBudgets, icon: PiggyBank, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Fixed Assets', path: ROUTES.accountsAssets, icon: Warehouse, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Sales Commissions', path: ROUTES.accountingV2Commissions, icon: Banknote, depts: ['accounts'], roles: ['admin', 'superadmin'], access: 'accounts', deptEnforced: true },
      { label: 'Audit Trail Logs', path: ROUTES.accountingV2AuditLogs, icon: ShieldAlert, depts: ['accounts'], roles: ['admin', 'superadmin'], access: 'accounts', deptEnforced: true },
    ],
  },
  {
    label: 'Accounts Administration',
    items: [
      { label: 'Accounts Settings', path: ROUTES.accountingV2Settings, icon: Settings, depts: ['accounts'], roles: ['admin', 'superadmin'], access: 'accounts', deptEnforced: true },
      { label: 'Team & Roles', path: ROUTES.accountsTeam, icon: UsersRound, depts: ['accounts'], roles: ['admin', 'superadmin'], access: 'accounts', deptEnforced: true },
      { label: 'Transaction Locking', path: ROUTES.accountsTransactionLocking, icon: Lock, depts: ['accounts'], roles: ['admin', 'superadmin'], access: 'accounts', deptEnforced: true },
      { label: 'Bulk Update Tool', path: ROUTES.accountsBulkUpdate, icon: ListChecks, depts: ['accounts'], roles: ['admin', 'superadmin'], access: 'accounts', deptEnforced: true },
      { label: 'Currency Adjustments', path: ROUTES.accountsCurrencyAdjustments, icon: Coins, depts: ['accounts'], roles: ['admin', 'superadmin'], access: 'accounts', deptEnforced: true },
      { label: 'CSV Data Imports', path: ROUTES.accountsImports, icon: FileUp, depts: ['accounts'], roles: ['admin', 'superadmin'], access: 'accounts', deptEnforced: true },
      { label: 'Cross-Entity Search', path: ROUTES.accountsSearch, icon: Search, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Document Inbox', path: ROUTES.accountsDocuments, icon: ScanLine, depts: ['accounts'], access: 'accounts', deptEnforced: true },
      { label: 'Time Tracking & Billing', path: ROUTES.accountsTimeTracking, icon: Clock, depts: ['accounts'], access: 'accounts', deptEnforced: true },
    ],
  },

  // ── PRODUCTION & SERVICES ─────────────────────────────────────────────────
  {
    label: 'Manufacturing',
    items: [
      { label: 'Manufacturing Dashboard', path: ROUTES.manufacturing, icon: LayoutDashboard, zynxIcon: 'dashboard', access: 'manufacturing' },
      { label: 'Production Orders', path: ROUTES.manufacturingOrders, icon: ClipboardList, zynxIcon: 'orders', access: 'manufacturing' },
      { label: 'BOMs & Formulas', path: ROUTES.manufacturingBoms, icon: FileText, access: 'manufacturing' },
      { label: 'Batch Operations', path: ROUTES.manufacturingBatchOps, icon: FolderKanban, access: 'manufacturing' },
      { label: 'Bulk & Packaging', path: ROUTES.manufacturingPackaging, icon: Package, access: 'manufacturing' },
      { label: 'Quality Control', path: ROUTES.manufacturingQuality, icon: CheckSquare, access: 'manufacturing' },
      { label: 'Batch Traceability', path: ROUTES.manufacturingTraceability, icon: Search, access: 'manufacturing' },
      { label: 'Production Master Data', path: ROUTES.manufacturingMasterData, icon: Settings, access: 'manufacturing' },
      { label: 'Outputs & Costing', path: ROUTES.manufacturingCosting, icon: BarChart3, access: 'manufacturing' },
      { label: 'Oil Scenario Runner', path: ROUTES.manufacturingOilScenario, icon: BarChart3, access: 'manufacturing' },
    ],
  },
  {
    label: 'Services & Projects',
    // Each item carries its own module key, so a department that holds delivery
    // access does not automatically see pricing or margin.
    items: [
      { label: 'Rate Cards', path: ROUTES.svcRateCards, icon: ReceiptText, access: 'rate_cards' },
      { label: 'Proposals & SOWs', path: ROUTES.svcProposals, icon: FileText, access: 'proposals' },
      { label: 'Contracts', path: ROUTES.svcContracts, icon: BriefcaseBusiness, access: 'contracts' },
      { label: 'Projects & Campaigns', path: ROUTES.svcProjects, icon: FolderKanban, access: 'services_projects' },
      { label: 'Timesheets', path: ROUTES.svcTimesheets, icon: CalendarClock, access: 'timesheets' },
      { label: 'Billing', path: ROUTES.svcBilling, icon: Receipt, access: 'billing', excludeDepts: ['accounts'] },
      { label: 'Revenue Recognition', path: ROUTES.svcRevenueRecognition, icon: ArrowLeftRight, access: 'revenue_recognition', excludeDepts: ['accounts'] },
      { label: 'Profitability', path: ROUTES.svcProfitability, icon: BarChart3, access: 'profitability', excludeDepts: ['accounts'] },
    ],
  },

  // ── PEOPLE ────────────────────────────────────────────────────────────────
  {
    label: 'HR & Payroll',
    items: [
      { label: 'HR Dashboard', path: ROUTES.hrDashboard, icon: Users, zynxIcon: 'dashboard', depts: ['hr', 'accounts'], roles: ['admin', 'superadmin', 'manager', 'team_lead'], access: 'hr' },
      { label: 'Attendance', path: ROUTES.attendance, icon: CalendarClock, zynxIcon: 'attendance', access: 'hr' },
      { label: 'Leaves', path: ROUTES.leaves, icon: CalendarRange, zynxIcon: 'leaves', access: 'hr' },
      { label: 'Payroll', path: ROUTES.payroll, icon: Wallet, zynxIcon: 'payroll', depts: ['hr', 'accounts'], access: 'payroll', deptEnforced: true, deptRoles: { accounts: ['admin', 'department_admin'] } },
      { label: 'Performance', path: ROUTES.performance, icon: LineChart, depts: ['hr', 'accounts'], access: 'hr' },
      { label: 'Incentives', path: ROUTES.incentives, icon: Banknote, depts: ['hr', 'sales', 'accounts'], roles: ['admin', 'superadmin', 'manager', 'team_lead'], access: 'hr' },
      { label: 'Rewards', path: ROUTES.rewards, icon: Award, zynxIcon: 'reward', access: 'hr' },
      { label: 'Tax Declarations', path: ROUTES.taxDeclarations, icon: FileSpreadsheet, access: 'hr' },
      { label: 'Full & Final Settlement', path: ROUTES.fnfSettlement, icon: UserMinus, access: 'hr' },
      { label: 'Recruitment', path: ROUTES.recruitment, icon: BriefcaseBusiness, depts: ['hr', 'accounts'], access: 'hr' },
      { label: 'Offer Letters', path: ROUTES.offerLetters, icon: FileText, depts: ['hr', 'accounts'], roles: ['admin', 'superadmin'], access: 'hr' },
      { label: 'Onboarding', path: ROUTES.hrOnboarding, icon: ClipboardList, depts: ['hr', 'accounts'], roles: ['admin', 'superadmin'], access: 'hr' },
      { label: 'Document Templates', path: ROUTES.hrDocumentTemplates, icon: FileText, depts: ['hr', 'accounts'], roles: ['admin', 'superadmin'], access: 'hr' },
      { label: 'Letterhead Branding', path: ROUTES.hrLetterheadBranding, icon: Palette, depts: ['hr', 'accounts'], roles: ['admin', 'superadmin'], access: 'hr' },
      { label: 'Biometric Devices', path: ROUTES.biometricAdmin, icon: Fingerprint, depts: ['hr', 'accounts'], roles: ['admin', 'superadmin'], access: 'hr' },
    ],
  },

  // ── EVERYDAY TOOLS ────────────────────────────────────────────────────────
  {
    label: 'Workspace',
    items: [
      { label: 'Tasks', path: ROUTES.tasks, icon: ClipboardList, zynxIcon: 'tasks', access: 'work' },
      { label: 'Meetings', path: ROUTES.meetings, icon: CalendarDays, access: 'work' },
      { label: 'Notepad', path: ROUTES.notes, icon: NotebookPen, access: 'work' },
      { label: 'Projects', path: ROUTES.projects, icon: FolderKanban, access: 'work' },
      { label: 'Project Demos', path: ROUTES.demos, icon: MonitorPlay, access: 'work' },
      { label: 'ZYNX Chat', path: ROUTES.chat, icon: MessagesSquare, zynxIcon: 'chat', access: 'communication' },
      { label: 'Announcements', path: ROUTES.announcements, icon: Megaphone, access: 'communication' },
      { label: 'Mail Inbox', path: ROUTES.mailInbox, icon: Inbox },
      { label: 'Service Desk', path: ROUTES.serviceDesk, icon: LifeBuoy, zynxIcon: 'support', access: 'service_desk' },
      { label: 'AI Workspace', path: ROUTES.ai, icon: Sparkles, access: 'ai' },
      { label: 'AI Copilot', path: ROUTES.aiCopilot, icon: BrainCircuit, access: 'ai' },
    ],
  },

  // ── MANAGEMENT ────────────────────────────────────────────────────
  {
    label: 'Reports & Analytics',
    items: [
      { label: 'Analytics', path: ROUTES.analytics, icon: LineChart, zynxIcon: 'analytics', roles: ['admin', 'superadmin'], access: 'analytics' },
      { label: 'Reports', path: ROUTES.reports, icon: BarChart3, roles: ['admin', 'superadmin'], access: 'analytics' },
      { label: 'Report Suite', path: ROUTES.reportSuite, icon: FileText, access: 'analytics' },
      { label: 'Operational Reports', path: ROUTES.operationalReports, icon: BarChart3, roles: ['admin', 'superadmin'], access: 'analytics' },
    ],
  },
  {
    label: 'Oversight',
    items: [
      { label: 'Activity', path: ROUTES.activity, icon: Activity, access: 'communication' },
      { label: 'Live Oversight', path: ROUTES.oversight, icon: ShieldAlert, depts: ['sales'], access: 'communication' },
      { label: 'Communication Hub', path: ROUTES.commHub, icon: Radio, depts: ['sales'], access: 'communication' },
      { label: 'CEO Command Center', path: ROUTES.commandCenter, icon: ShieldAlert, authorityTiers: ['SUPER_ADMIN'] },
    ],
  },
  {
    label: 'Administration',
    items: [
      // Gated by authority tier, not by the `hr` workspace module: this is the
      // screen that GRANTS workspace modules, so requiring one to open it meant
      // only the owner could ever add a person or widen their access. The server
      // still decides (requireAdmin + own-department scoping).
      { label: 'Team & Access', path: ROUTES.hr, icon: Users, zynxIcon: 'contacts', roles: ['admin', 'superadmin'] },
      { label: 'Roles & Permissions', path: ROUTES.roles, icon: Shield, roles: ['admin', 'superadmin'] },
      { label: 'Organization', path: ROUTES.organization, icon: Network, roles: ['admin', 'superadmin'] },
      { label: 'Master Data', path: ROUTES.masterData, icon: Database, roles: ['admin', 'superadmin'] },
      { label: 'Settings', path: ROUTES.settings, icon: Settings, zynxIcon: 'settings' },
      { label: 'Communication Hub', path: ROUTES.commHub, icon: Radio, roles: ['admin', 'superadmin'] },
      { label: 'BizMail Campaigns', path: ROUTES.campaigns, icon: Mail, roles: ['admin', 'superadmin'] },
      { label: 'Notifications', path: ROUTES.notificationTemplates, icon: Megaphone, roles: ['admin', 'superadmin'], access: 'communication' },
      { label: 'Workflow Builder', path: ROUTES.workflowsAutomations, icon: Network, roles: ['admin', 'superadmin'] },
      { label: 'Portal Accounts', path: ROUTES.portalAccounts, icon: UserCog, roles: ['admin', 'superadmin'] },
      { label: 'Admin Tools', path: ROUTES.adminTools, icon: ArrowLeftRight, roles: ['admin', 'superadmin'] },
      // Super Admin only — what a "Move to Trash" hid is invisible to everyone else.
      { label: 'Pipeline Trash', path: ROUTES.pipelineTrash, icon: Trash2, roles: ['superadmin'] },
    ],
  },
];

// ── Stealth nav gating (Task 9.2 / 9.3) ──────────────────────────────────────
export function navRoleVisible(
  item: NavVisibilityPolicy,
  role: string | null | undefined,
  department?: string | null,
  workspaceAccess?: readonly WorkspaceAccessKey[],
  actionPermissions?: readonly string[] | null,
  user?: { username?: string | null; department?: string | null; role?: string | null } | null,
): boolean {
  // An item pinned to an authority tier belongs to that tier and nothing else.
  // This was never enforced here — only by the RoleRoute on click — so a
  // department administrator saw the CEO Command Center's label and path in the
  // menu even though the route itself refused them. EA-11 asks for no label, no
  // path, no discovery, so the gate has to be on the menu entry too.
  if (item.authorityTiers && !item.authorityTiers.includes(tierOf(role))) return false;

  // Company-wide navigation is an EXPLICIT management department, never a missing
  // one: an administrator whose department was left unset navigates by the same
  // rules as any other role in their department rather than seeing everything.
  if (tierOf(role) === 'SUPER_ADMIN' || (role === 'admin' && department === 'management')) {
    if (item.roles && !(role && item.roles.includes(role as (typeof item.roles)[number]))) {
      return false; // Respect explicit role restrictions like ['superadmin'] on Pipeline Trash
    }
    return true; // executive / management admin tier spans every department
  }

  const hasActionGrant = Array.isArray(actionPermissions) && !!item.path && navActionMatchesPath(actionPermissions, item.path);
  const isAccountsLead = user?.username === 'account' || user?.username === 'accounts' || user?.department === 'accounts' || /abhij/i.test(user?.username || '');
  const isAccountsSectionItem = Boolean(
    item.deptEnforced ||
    (item.depts && (item.depts.includes('accounts') || item.depts.includes('hr'))) ||
    item.access === 'hr' ||
    item.access === 'payroll' ||
    item.access === 'accounts' ||
    (Array.isArray(item.access) && (item.access.includes('hr') || item.access.includes('payroll') || item.access.includes('accounts'))) ||
    (item.path && (item.path.startsWith('/hr') || item.path.startsWith('/payroll') || item.path.startsWith('/accounts') || item.path.startsWith('/attendance') || item.path.startsWith('/leaves') || item.path.startsWith('/catalog')))
  );
  // Accounts user gets full access to all accounts and HR features ("ACCOUNT WALE KO HR KA SARA ACCESS DO")
  const rbacAllowed = hasActionGrant || (isAccountsLead && isAccountsSectionItem) || isRbacAllowed(role, item);
  if (!rbacAllowed) return false;

  const departmentAllowed = !item.excludeDepts || !department || !item.excludeDepts.includes(department);
  if (!departmentAllowed && !hasActionGrant && !(isAccountsLead && isAccountsSectionItem)) return false;

  const deptRoles = department ? item.deptRoles?.[department] : undefined;
  if (deptRoles && !deptRoles.includes(role ?? '') && !hasActionGrant && !(isAccountsLead && isAccountsSectionItem)) return false;
  if (item.deptEnforced && item.depts && !(department && item.depts.includes(department)) && !hasActionGrant && !(isAccountsLead && isAccountsSectionItem)) {
    return false;
  }

  if (item.access) {
    // `access` may name several modules — the item is visible if the caller
    // holds ANY of them, or holds an explicit action grant for this screen.
    const required = Array.isArray(item.access) ? item.access : [item.access];
    let hasModuleAccess = hasActionGrant || (isAccountsLead && isAccountsSectionItem);
    if (!hasModuleAccess) {
      if (workspaceAccess && workspaceAccess.length > 0) {
        hasModuleAccess = required.some((key) => workspaceAccess.includes(key));
      } else if (department) {
        const defaults = defaultWorkspaceAccess(department);
        hasModuleAccess = required.some((key) => defaults.includes(key));
      }
    }

    if (!hasModuleAccess) {
      return false;
    }
    // Also enforce department restrictions if item specifies depts, unless explicitly granted
    const isSalesDeptOrBdm = department === 'sales' || department === 'general_manager' || Boolean(user?.username && String(user.username).toLowerCase().startsWith('bdm'));
    const isDeptMatch = !item.depts || (!!department && item.depts.includes(department)) || (item.depts.includes('sales') && isSalesDeptOrBdm) || (isAccountsLead && isAccountsSectionItem);
    if (item.depts && !isDeptMatch && !hasActionGrant && !(isAccountsLead && isAccountsSectionItem)) {
      return false;
    }
    return true;
  }

  const isSalesDeptOrBdm = department === 'sales' || department === 'general_manager' || Boolean(user?.username && String(user.username).toLowerCase().startsWith('bdm'));
  const isDeptMatch = !item.depts || (!!department && item.depts.includes(department)) || (item.depts.includes('sales') && isSalesDeptOrBdm) || (isAccountsLead && isAccountsSectionItem);
  const deptOk = hasActionGrant || isDeptMatch || (isAccountsLead && isAccountsSectionItem);
  return deptOk;
}

/** All nav groups with items filtered to the caller's role (empty groups dropped). */
/**
 * Whether a nav item survives the section gate.
 *
 * Deliberately implemented here rather than imported from moduleSections.ts:
 * that module derives its section list FROM NAV_GROUPS, so importing it back
 * would create a cycle whose initialisation order is fragile. A nav item *is*
 * a section — its path is the section identity — so matching the path against
 * the granted list directly is both simpler and exact.
 *
 * `null` / `undefined` means unrestricted, matching grantedSections().
 */
function navSectionVisible(granted: readonly string[] | null | undefined, path: string): boolean {
  if (!granted) return true;
  return granted.includes(path);
}

const PATH_ACTION_PREFIX_MAP: Record<string, string[]> = {
  // Sales / CRM Pipeline
  [ROUTES.pipeline]: ['leads', 'deals'],
  [ROUTES.pipelineNew]: ['leads', 'deals'],
  [ROUTES.pipelineDetail]: ['leads', 'deals'],
  [ROUTES.pipelineEdit]: ['leads', 'deals'],
  [ROUTES.leads]: ['leads'],
  [ROUTES.sampling]: ['sampling'],
  [ROUTES.samplingNew]: ['sampling'],
  [ROUTES.samplingEdit]: ['sampling'],
  [ROUTES.deals]: ['deals'],
  [ROUTES.dealDetail]: ['deals'],
  [ROUTES.contacts]: ['contacts'],
  [ROUTES.contactNew]: ['contacts'],
  [ROUTES.contactDetail]: ['contacts'],
  [ROUTES.contactEdit]: ['contacts'],
  [ROUTES.quotations]: ['quotation'],
  [ROUTES.proforma]: ['proforma'],
  [ROUTES.invoices]: ['invoice'],
  [ROUTES.invoiceEditApprovals]: ['invoice_edit_requests', 'invoice'],
  [ROUTES.documentEditor]: ['quotation', 'proforma', 'sales', 'invoice'],
  [ROUTES.salesDocs]: ['sales'],
  [ROUTES.salesOperations]: ['sales', 'sales_ops'],
  [ROUTES.salesOrderDetail]: ['sales', 'sales_ops'],
  [ROUTES.salesDispatchTracking]: ['dispatch', 'sales', 'sales_ops', 'inventory', 'accounts', 'accounting'],
  [ROUTES.dispatchOrders]: ['dispatch', 'challan', 'sales', 'sales_ops', 'inventory', 'manufacturing'],
  [ROUTES.dispatchChallans]: ['challan', 'dispatch', 'sales'],
  [ROUTES.dispatchLocalTransport]: ['dispatch', 'challan', 'sales', 'sales_ops', 'inventory', 'manufacturing', 'accounts'],
  [ROUTES.fieldApprovals]: ['field_expense', 'dispatch'],
  [ROUTES.visits]: ['visits'],
  [ROUTES.liveTracking]: ['visits', 'sales'],
  [ROUTES.fleetManagement]: ['fleet', 'dispatch'],
  [ROUTES.driverPortal]: ['fleet', 'dispatch'],
  [ROUTES.vouchers]: ['vouchers', 'sales'],
  [ROUTES.loyalty]: ['loyalty', 'sales'],
  [ROUTES.clients]: ['clients'],
  [ROUTES.targets]: ['targets'],
  [ROUTES.salesPerformance]: ['targets', 'reports', 'sales'],
  [ROUTES.complaints]: ['complaints', 'sales', 'service_desk'],
  [ROUTES.leadInbox]: ['lead_inbox', 'integrations', 'sales'],
  [ROUTES.websiteLeads]: ['lead_inbox', 'leads', 'sales'],
  [ROUTES.websiteDocuments]: ['templates', 'products', 'sales'],
  [ROUTES.pos]: ['pos'],

  // Inventory & Products
  [ROUTES.catalog]: ['products', 'catalog', 'sales'],
  // Dispatch staff raise material requests and record inward here (client,
  // 2026-10-24) — the same action keys that open the Dispatch screens.
  [ROUTES.inventory]: ['inventory', 'dispatch', 'challan'],
  [ROUTES.inventoryAnalysis]: ['inventory'],
  [ROUTES.accountingV2Items]: ['products', 'inventory'],
  [ROUTES.accountingV2Barcode]: ['products', 'inventory'],
  [ROUTES.accountingV2Rejections]: ['inventory', 'stock_transfer'],

  // Accounts & Finance
  [ROUTES.accountingV2]: ['cashbook', 'accounting'],
  [ROUTES.accountsSales]: ['invoice', 'sales'],
  [ROUTES.accountsHandoffs]: ['invoice', 'sales'],
  [ROUTES.accountingV2Vouchers]: ['accounting_vouchers', 'vouchers', 'cashbook'],
  [ROUTES.accountingV2Pdc]: ['pdc', 'cashbook'],
  [ROUTES.accountingV2Parties]: ['contacts', 'clients', 'cashbook'],
  [ROUTES.bankingReconciliation]: ['reconcile', 'cashbook'],
  [ROUTES.accountingV2Expenses]: ['expenses', 'cashbook'],
  [ROUTES.accountingV2Approvals]: ['invoice', 'expenses', 'cashbook'],
  [ROUTES.accountingV2P2p]: ['purchase_order', 'purchase'],
  [ROUTES.accountingV2Rfqs]: ['purchase_order', 'purchase'],
  [ROUTES.accountingV2SupplierScorecards]: ['purchase', 'contacts'],
  [ROUTES.gstFiling]: ['taxes', 'reports'],
  [ROUTES.accountingGstr1]: ['taxes', 'reports'],
  [ROUTES.accountingGstr2]: ['taxes', 'reports'],
  [ROUTES.accountingGstr3]: ['taxes', 'reports'],
  [ROUTES.ewayBill]: ['eway_bill', 'invoice'],
  [ROUTES.accountingV2Coa]: ['coa', 'cashbook'],
  [ROUTES.accountingV2Transactions]: ['cashbook', 'journals'],
  [ROUTES.accountingV2Journals]: ['journals', 'cashbook'],
  [ROUTES.accountingV2Reports]: ['reports', 'cashbook'],
  [ROUTES.accountsBankCashReports]: ['reports', 'cashbook'],
  [ROUTES.accountsAgingReports]: ['reports', 'cashbook'],
  [ROUTES.accountsBudgets]: ['cashbook', 'expenses'],
  [ROUTES.accountsAssets]: ['employee', 'cashbook'],
  [ROUTES.accountingV2Commissions]: ['payroll', 'sales'],
  [ROUTES.accountingV2AuditLogs]: ['audit_logs', 'administration'],
  [ROUTES.accountingV2Settings]: ['settings', 'administration'],
  [ROUTES.accountsTeam]: ['administration', 'hr'],
  [ROUTES.accountsTransactionLocking]: ['settings', 'cashbook'],
  [ROUTES.accountsBulkUpdate]: ['administration', 'masters'],
  [ROUTES.accountsCurrencyAdjustments]: ['cashbook', 'settings'],
  [ROUTES.accountsImports]: ['masters', 'administration'],
  [ROUTES.accountsSearch]: ['cashbook'],
  [ROUTES.accountsDocuments]: ['templates', 'cashbook'],
  [ROUTES.accountsTimeTracking]: ['attendance', 'tasks'],

  // Manufacturing
  [ROUTES.manufacturing]: ['manufacturing'],
  [ROUTES.manufacturingOrders]: ['manufacturing'],
  [ROUTES.manufacturingBoms]: ['manufacturing'],
  [ROUTES.manufacturingBatchOps]: ['manufacturing'],
  [ROUTES.manufacturingPackaging]: ['manufacturing'],
  [ROUTES.manufacturingQuality]: ['manufacturing'],
  [ROUTES.manufacturingTraceability]: ['manufacturing'],
  [ROUTES.manufacturingMasterData]: ['manufacturing'],
  [ROUTES.manufacturingCosting]: ['manufacturing'],
  [ROUTES.manufacturingOilScenario]: ['manufacturing'],

  // Services & Projects
  [ROUTES.svcRateCards]: ['rate_cards', 'services_projects', 'pricing'],
  [ROUTES.svcProposals]: ['proposals', 'services_projects', 'quotation'],
  [ROUTES.svcContracts]: ['contracts', 'services_projects'],
  [ROUTES.svcProjects]: ['projects', 'services_projects'],
  [ROUTES.svcTimesheets]: ['timesheets', 'services_projects', 'attendance'],
  [ROUTES.svcBilling]: ['billing', 'services_projects', 'invoice'],
  [ROUTES.svcRevenueRecognition]: ['reports', 'services_projects'],
  [ROUTES.svcProfitability]: ['reports', 'services_projects'],

  // Organization & HR
  [ROUTES.attendance]: ['attendance'],
  // ROUTES.leaves deliberately absent: every employee applies for leave, so no
  // permission key may hide it (the HR-menu copy keeps its own `access: 'hr'`).
  [ROUTES.biometricAdmin]: ['biometrics', 'attendance'],
  [ROUTES.performance]: ['performance', 'hr'],
  [ROUTES.incentives]: ['incentives', 'hr', 'targets'],
  [ROUTES.payroll]: ['payroll', 'hr'],
  [ROUTES.hr]: ['hr', 'administration', 'employee'],
  [ROUTES.hrDashboard]: ['hr', 'employee'],
  [ROUTES.roles]: ['administration'],
  [ROUTES.itOperations]: ['it', 'service_desk'],
  [ROUTES.organization]: ['departments', 'hr', 'administration'],
  [ROUTES.recruitment]: ['recruitment', 'hr'],
  [ROUTES.offerLetters]: ['recruitment', 'hr'],
  [ROUTES.hrOnboarding]: ['recruitment', 'hr'],
  [ROUTES.hrDocumentTemplates]: ['templates', 'hr'],
  [ROUTES.hrLetterheadBranding]: ['settings', 'hr'],
  [ROUTES.rewards]: ['hr'],
  [ROUTES.taxDeclarations]: ['payroll', 'hr'],
  [ROUTES.fnfSettlement]: ['payroll', 'hr'],

  // Work & Communication
  [ROUTES.campaigns]: ['campaigns'],
  [ROUTES.serviceDesk]: ['service_desk'],
  [ROUTES.tasks]: ['tasks'],
  [ROUTES.chat]: ['communication'],
  [ROUTES.notes]: ['notepad', 'notes'],
  [ROUTES.scraper]: ['scraper'],
  [ROUTES.callCenter]: ['call_center', 'communication'],
  [ROUTES.meetings]: ['appointments'],
  [ROUTES.projects]: ['projects'],
  [ROUTES.demos]: ['projects'],
  [ROUTES.announcements]: ['communication'],
  [ROUTES.mailInbox]: ['mailbox', 'communication'],
  [ROUTES.ai]: ['ai'],
  [ROUTES.aiCopilot]: ['ai'],

  // Reports & Analytics
  [ROUTES.analytics]: ['reports'],
  [ROUTES.reports]: ['reports'],
  [ROUTES.reportSuite]: ['reports'],
  [ROUTES.operationalReports]: ['reports'],

  // Oversight & Administration
  [ROUTES.activity]: ['communication', 'administration'],
  [ROUTES.oversight]: ['oversight', 'administration'],
  [ROUTES.commandCenter]: ['command_center', 'administration'],
  [ROUTES.masterData]: ['masters', 'administration'],
  [ROUTES.settings]: ['settings', 'administration'],
  [ROUTES.commHub]: ['communication', 'administration'],
  [ROUTES.notificationTemplates]: ['templates', 'communication'],
  [ROUTES.workflowsAutomations]: ['workflows', 'administration'],
  [ROUTES.portalAccounts]: ['portal_accounts', 'administration'],
  [ROUTES.adminTools]: ['admin_tools', 'administration'],
  [ROUTES.pipelineTrash]: ['trash', 'administration'],
};

export function navActionMatchesPath(actionPermissions: readonly string[] | null | undefined, path: string): boolean {
  if (!Array.isArray(actionPermissions)) return false;
  const prefixes = PATH_ACTION_PREFIX_MAP[path];
  if (!prefixes || prefixes.length === 0) return false;

  return prefixes.some((prefix) =>
    actionPermissions.some((key) => key.startsWith(`${prefix}.`) || key === prefix),
  );
}

export function navActionVisible(actionPermissions: readonly string[] | null | undefined, path: string): boolean {
  if (!Array.isArray(actionPermissions)) return true;
  const prefixes = PATH_ACTION_PREFIX_MAP[path];
  if (!prefixes || prefixes.length === 0) return true;

  return prefixes.some((prefix) =>
    actionPermissions.some((key) => key.startsWith(`${prefix}.`) || key === prefix),
  );
}

// A driver account (role: 'driver', created via POST /api/fleet/drivers) was
// previously getting the full 'dispatch' department's workspace access —
// sales, sales_operations, inventory, work, service_desk, communication —
// on their own desktop login, none of which a driver needs (client-reported:
// "driver ka account banaya, uske desktop login pe CRM ke features nahi
// chahiye"). This is their entire sidebar: just the portal that shows their
// assigned delivery, route, and live map.
const DRIVER_NAV_GROUPS: NavGroup[] = [
  { items: [{ label: 'My Deliveries', path: ROUTES.driverPortal, icon: Truck }] },
];

// A Field Executive (client, 2026-10-07) works in the field: their own visits,
// their own and assigned contacts, leads, follow-ups, samples and invoice
// REQUESTS — nothing else. This is their entire sidebar.
export const FIELD_NAV_GROUPS: NavGroup[] = [
  { items: [{ label: 'My Dashboard', path: ROUTES.dashboard, icon: LayoutDashboard }, { label: 'Leave Request', path: ROUTES.leaves, icon: CalendarRange }] },
  {
    label: 'Field work',
    items: [
      { label: 'Visit Planner', path: ROUTES.visits, icon: MapPinned, zynxIcon: 'visit' },
      { label: 'Live GPS Radar', path: ROUTES.liveTracking, icon: Radio },
      { label: 'My Contacts', path: ROUTES.contacts, icon: ContactsIcon },
      { label: 'My Leads', path: ROUTES.pipeline, icon: Target },
      { label: 'Sampling & Trials', path: ROUTES.sampling, icon: FlaskConical, zynxIcon: 'trial' },
      { label: 'Invoice Requests', path: ROUTES.fieldInvoiceRequests, icon: ReceiptText },
    ],
  },
  {
    label: 'Me',
    items: [{ label: 'Tasks', path: ROUTES.tasks, icon: CheckSquare }],
  },
];

/** Screens a Field Executive may open (anything else sends them home). */
const FIELD_ALLOWED_PREFIXES = ['/dashboard', '/field', '/visits', '/tracking', '/live-tracking', '/contacts', '/pipeline', '/sampling', '/tasks', '/my-profile', '/notifications', '/leaves'];
export function fieldExecutiveMayOpen(pathname: string): boolean {
  if (pathname === '/' || pathname === '') return true;
  return FIELD_ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

// Rohit Sharma (asm1, asm2, Area Sales Manager):
// Client requirements:
//  - Sales Pipeline (only assigned/created leads)
//  - Contacts (only assigned/created contacts)
//  - Client Profiles (only assigned/created clients)
//  - Sampling & Trials (only assigned/created samples)
//  - Quotations (only assigned/created quotations)
//  - Proforma Invoices (only assigned/created proformas)
//  - Invoices (approval required)
//  - Visit Planner (field visits)
//  - ZYNX Chat
//  - Mail Inbox
//  - Hide all other department modules completely!
export { isRohitSharmaUser };

export const ROHIT_SHARMA_NAV_GROUPS: NavGroup[] = [
  { items: [{ label: 'Dashboard', path: ROUTES.dashboard, icon: LayoutDashboard }] },
  {
    label: 'Sales Pipeline',
    items: [
      { label: 'Sales Pipeline', path: ROUTES.pipeline, icon: Target },
      { label: 'Contacts', path: ROUTES.contacts, icon: ContactsIcon },
      { label: 'Client Profiles', path: ROUTES.clients, icon: Building2 },
      { label: 'Sampling & Trials', path: ROUTES.sampling, icon: FlaskConical, zynxIcon: 'trial' },
      { label: 'Quotations', path: ROUTES.quotations, icon: FileText, zynxIcon: 'quote' },
      { label: 'Proforma Invoices', path: ROUTES.proforma, icon: FileStack },
      { label: 'Invoices', path: ROUTES.invoices, icon: ReceiptText, zynxIcon: 'invoice' },
    ],
  },
  {
    label: 'Field Sales',
    items: [
      { label: 'Visit Planner', path: ROUTES.visits, icon: MapPinned, zynxIcon: 'visit' },
      { label: 'Live GPS Radar', path: ROUTES.liveTracking, icon: Radio },
    ],
  },
  {
    label: 'Workspace',
    items: [
      { label: 'Tasks', path: ROUTES.tasks, icon: ClipboardList },
      { label: 'Meetings', path: ROUTES.meetings, icon: CalendarDays },
      { label: 'Notepad', path: ROUTES.notes, icon: NotebookPen },
      { label: 'Projects', path: ROUTES.projects, icon: FolderKanban },
      { label: 'Project Demos', path: ROUTES.demos, icon: MonitorPlay },
      { label: 'ZYNX Chat', path: ROUTES.chat, icon: MessagesSquare },
      { label: 'Announcements', path: ROUTES.announcements, icon: Megaphone },
      { label: 'Mail Inbox', path: ROUTES.mailInbox, icon: Inbox },
      { label: 'Service Desk', path: ROUTES.serviceDesk, icon: LifeBuoy, zynxIcon: 'support' },
      { label: 'AI Workspace', path: ROUTES.ai, icon: Sparkles },
      { label: 'AI Copilot', path: ROUTES.aiCopilot, icon: BrainCircuit },
    ],
  },
  {
    label: 'Communication & Oversight',
    items: [
      { label: 'Activity', path: ROUTES.activity, icon: Activity },
      { label: 'Communication Hub', path: ROUTES.commHub, icon: Radio },
    ],
  },
];

const ROHIT_SHARMA_ALLOWED_PREFIXES = [
  '/dashboard',
  '/pipeline',
  '/contacts',
  '/clients',
  '/sampling',
  '/quotations',
  '/proforma',
  '/invoices',
  '/field-invoice-requests',
  '/visits',
  '/live-tracking',
  '/chat',
  '/announcements',
  '/mail-inbox',
  '/tasks',
  '/meetings',
  '/notes',
  '/projects',
  '/demos',
  '/service-desk',
  '/ai',
  '/ai-copilot',
  '/activity',
  '/communication-hub',
  '/my-profile',
  '/notifications',
  '/document-editor',
];

export function rohitSharmaMayOpen(pathname: string): boolean {
  if (pathname === '/' || pathname === '') return true;
  return ROHIT_SHARMA_ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function isSalesOrBdmUser(
  user?: { username?: string | null; department?: string | null; role?: string | null } | null,
  department?: string | null,
  role?: string | null,
): boolean {
  const effectiveRole = role || user?.role;
  if (tierOf(effectiveRole) === 'SUPER_ADMIN' || tierOf(effectiveRole) === 'ADMIN') return false;
  const dept = String(department || user?.department || '').trim().toLowerCase();
  const uname = String(user?.username || '').trim().toLowerCase();
  if (dept === 'sales') return true;
  if (uname.startsWith('bdm')) return true;
  return false;
}

const SALES_BDM_ALLOWED_GROUPS = new Set([
  'Sales',
  'Leads & Marketing',
  'Field Sales',
  'Dispatch',
  'Purchases',
  'Workspace',
  'Oversight',
]);

/**
 * Sidebar for one identity.
 *
 * Three gates run in order:
 *  1. MODULE — role / department / workspace-access decide whether the user holds the module.
 *  2. SECTION — `grantedSections` may narrow that module to specific screens.
 *  3. ACTION — `actionPermissions` ensures if an administrator configured "none" for a module, it is hidden.
 */
export function navGroupsForRole(
  role: string | null | undefined,
  department?: string | null,
  workspaceAccess?: readonly WorkspaceAccessKey[],
  grantedSections?: readonly string[] | null,
  actionPermissions?: readonly string[] | null,
  user?: { username?: string | null; display_name?: string | null; displayName?: string | null; role?: string | null; department?: string | null } | null,
): NavGroup[] {
  if (isRohitSharmaUser(user)) {
    return ROHIT_SHARMA_NAV_GROUPS
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (it) =>
            navSectionVisible(grantedSections, it.path) &&
            navActionVisible(actionPermissions, it.path),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }
  if (role === 'driver') return DRIVER_NAV_GROUPS;
  if (role === 'field_executive') return FIELD_NAV_GROUPS;
  // Only the executive tier short-circuits to the whole menu. A management
  // administrator used to do the same here — a third copy of the `!department`
  // widening — which handed over the executive-only entries without ever
  // consulting navRoleVisible. They now take the filtered path below, where
  // navRoleVisible still grants them every non-executive surface.
  if (tierOf(role) === 'SUPER_ADMIN') return NAV_GROUPS;

  const allowedGroups = isSalesOrBdmUser(user, department, role)
    ? NAV_GROUPS.filter((g) => !g.label || SALES_BDM_ALLOWED_GROUPS.has(g.label))
    : NAV_GROUPS;

  return allowedGroups
    .map((g) => ({
      ...g,
      items: g.items.filter(
        (it) =>
          navRoleVisible(it, role, department, workspaceAccess, actionPermissions, user) &&
          navSectionVisible(grantedSections, it.path) &&
          navActionVisible(actionPermissions, it.path),
      ),
    }))
    .filter((g) => g.items.length > 0);
}

const NOTIFICATION_TARGETS = new Set<string>([ROUTES.incentives, ROUTES.invoiceEditApprovals]);

/**
 * Whether a URL that IS a sidebar entry passes the same module + action gates
 * the sidebar applies (the section gate runs separately in SectionRoute).
 * Without this, a screen hidden from the sidebar still opened when its URL was
 * typed in and then 403'd on every request. Exact path match only: detail and
 * child routes are not sidebar entries and stay governed by their own route
 * guards and the server, exactly as before.
 */
export function navPathAllowed(
  pathname: string,
  role: string | null | undefined,
  department?: string | null,
  workspaceAccess?: readonly WorkspaceAccessKey[],
  actionPermissions?: readonly string[] | null,
  user?: { username?: string | null; display_name?: string | null; displayName?: string | null; role?: string | null; department?: string | null } | null,
): boolean {
  // An unset department must not widen route access; 'management' is explicit.
  if (role === 'driver' || tierOf(role) === 'SUPER_ADMIN' || (role === 'admin' && department === 'management')) return true;
  if (isRohitSharmaUser(user)) {
    return rohitSharmaMayOpen(pathname);
  }
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (NOTIFICATION_TARGETS.has(path)) return true;
  const groups = isSalesOrBdmUser(user, department, role)
    ? NAV_GROUPS.filter((g) => !g.label || SALES_BDM_ALLOWED_GROUPS.has(g.label))
    : NAV_GROUPS;
  const items = groups.flatMap((g) => g.items).filter((it) => it.path === path);
  if (items.length === 0) {
    // If user is sales/BDM and the path belongs to another module's sidebar, disallow it
    if (isSalesOrBdmUser(user, department, role)) {
      const allOtherItems = NAV_GROUPS.flatMap((g) => g.items).filter((it) => it.path === path);
      if (allOtherItems.length > 0) return false;
    }
    return true;
  }
  if (!items.every((it) => it.routeGuarded || it.deptEnforced || it.deptRoles || it.roles || it.authorityTiers)) return true;
  return items.some((it) => navRoleVisible(it, role, department, workspaceAccess, actionPermissions, user) && navActionVisible(actionPermissions, it.path));
}

